import React, { FunctionComponent, useEffect, useState } from 'react'
import { RouteComponentProps, StaticContext } from 'react-router'

import { OpaOrderSummary } from './OpaOrderSummary'
import { OrderCancellationCodes } from '../../constants'
import { useAppContext } from '../../context/app'
import { OrderLineCancellationModal } from '../../features/OrderLineCancellationModal'
import { AnalyticsEvent } from '../../services/Analytics'
import analyticsServiceSingleton from '../../services/Analytics/initAnalytics'
import {
  AuthError,
  cancelGetOrderById,
  cancelGetProgramFromSearchIndexById,
  cancelGetReadOnlyOpaUrl,
  cancelRemoveOrder,
  cancelUpdateOrder,
  getOrderById,
  getProgramFromSearchIndexById,
  getReadOnlyOpaUrl
} from '../../services/ApiService'
import {
  OrderCancellationSaga,
  createOrderCancellationLogger,
  logOrderCancellationResult
} from '../../services/Saga'
import { useOrderLevelHolds } from '../../services/Holds/holds'
import { getHoldForType, HoldType } from '../../services/Holds/holds.constants'
import { IError } from '../../types'
import { findOrderCancellationLookupCode } from '../../types/IOrder'
import {
  DeleteOrderDto,
  OrderDto,
  ProgramCatalogDto
} from '../../types/swaggerTypes'
import { useErrorMessage } from '../../utils/useErrorMessage'
import { useOnMount } from '../../utils/useOnMount'
import {
  LocalStorageOPAKeys,
  isOrderCancellable
} from '../OpaCheckout/OpaCheckout.models'

interface IOpaOrderSummaryRouteParams {
  orderId: string
}

type LocationState = {
  endpoint?: string
  programId?: string
  from?: string
  patientId?: number
}

interface IOpaOrderSummaryProps
  extends RouteComponentProps<
    IOpaOrderSummaryRouteParams,
    StaticContext,
    LocationState
  > {}

export const OpaOrderSummaryContainer: FunctionComponent<
  IOpaOrderSummaryProps
> = ({ match, history }) => {
  const { orderId } = match.params
  const programId = history.location.state?.programId // || '2028' // for debug
  const [isOrderLoading, setIsOrderLoading] = useState<boolean>(true)
  const [isProgramLoading, setIsProgramLoading] = useState<boolean>(true)
  const [isLoadingPatientAccessForm, setIsLoadingPatientAccessForm] =
    useState(true)
  const [order, setOrder] = useState<OrderDto>()
  const [program, setProgram] = useState<ProgramCatalogDto>()
  const [deliveryNote, setDeliveryNote] = useState<string>('')
  const [patientAccessFormUrl, setPatientAccessFormUrl] = useState<
    string | undefined
  >()
  const [isCancelOrderlineModalOpen, setIsCancelOrderlineModalOpen] =
    useState<boolean>(false)
  const [deleteOrderDescription, setDeleteOrderDescription] = useState<
    DeleteOrderDto | undefined
  >()
  const [cancellationSubmitting, setCancellationSubmitting] =
    useState<boolean>(false)
  const cancellationOptions =
    OrderCancellationCodes && Object.values(OrderCancellationCodes)
  const [cancellationHasError, setCancellationHasError] = useState<IError>()
  const [isLoadingCancelOrder, setIsLoadingCancelOrder] =
    useState<boolean>(false)
  const { supportContact, institute } = useAppContext()
  const orderLevelHolds = useOrderLevelHolds(undefined, order, supportContact)

  const handleOrderError = useErrorMessage('There was an error fetching order.')
  const handleAccessFormUrlError = useErrorMessage(
    `There was an error fetching patient access form URL.`
  )
  const handleProgramError = useErrorMessage(
    `There was an error fetching program.`
  )

  const getDeliveryNote = () => {
    const existingDeliveryNote = localStorage.getItem(
      LocalStorageOPAKeys.DeliveryNote
    )
    if (existingDeliveryNote) {
      setDeliveryNote(existingDeliveryNote)
    }
  }

  const deleteDeliveryNote = () => {
    if (localStorage.getItem(LocalStorageOPAKeys.DeliveryNote))
      localStorage.removeItem(LocalStorageOPAKeys.DeliveryNote)
  }

  useEffect(() => {
    window.addEventListener('beforeunload', deleteDeliveryNote)
    return () => {
      deleteDeliveryNote()
      window.removeEventListener('beforeunload', deleteDeliveryNote)
    }
  }, [])

  useEffect(() => {
    orderLevelHolds.push(getHoldForType(HoldType.CustomDelivery))
  }, [orderLevelHolds])

  useOnMount(() => {
    orderId &&
      getOrderById(orderId)
        .then((response) => {
          setOrder(response.data)
        })
        .catch((error) => {
          handleOrderError(error)
        })
        .finally(() => setIsOrderLoading(false))
    programId &&
      getProgramFromSearchIndexById(programId)
        .then((result) => {
          setProgram(result.data)
        })
        .catch((error) => handleProgramError(error))
        .finally(() => setIsProgramLoading(false))
    getDeliveryNote()
    return () => {
      cancelGetOrderById()
      cancelGetProgramFromSearchIndexById()
      cancelUpdateOrder()
      cancelRemoveOrder()
    }
  })

  useEffect(() => {
    if (order?.opaAccessToken && order?.orderNumber) {
      getReadOnlyOpaUrl(Number(order?.orderNumber), {
        opaAccessToken: order?.opaAccessToken
      })
        .then((response) => {
          if (response.data.endPoint) {
            setPatientAccessFormUrl(response.data.endPoint)
          }
        })
        .catch((error) => handleAccessFormUrlError(error))
        .finally(() => setIsLoadingPatientAccessForm(false))
    } else if (order) {
      setIsLoadingPatientAccessForm(false)
    }
    return () => {
      cancelGetReadOnlyOpaUrl()
    }
  }, [handleAccessFormUrlError, order])

  const handlePatientAccessForm = () => {
    const physicianId = order?.lines[0].physician?.physicianId
    const programId = history.location.state?.programId
    patientAccessFormUrl
      ? history.push(
          `/programs/access-programs/${programId}/patient-access-form/${physicianId}`,
          {
            endpoint: patientAccessFormUrl,
            from: window.location.pathname
          }
        )
      : history.push('/about/contact-us')
  }

  const handlePlaceOrderAgain = () => {
    analyticsServiceSingleton.trackEvent(AnalyticsEvent.PlaceOrderMAAgain, {
      programId,
      orderId
    })
    history.push(`/programs/access-programs/${programId}`)
  }

  const handleViewDashboard = () => {
    history.push(`/orders`)
  }

  const handleCancelOrder = () => {
    const updateOrderLineDto: DeleteOrderDto = {
      cancelReason: '',
      cancelComments: ''
    }
    setDeleteOrderDescription(updateOrderLineDto)
    setIsCancelOrderlineModalOpen(true)
  }

  const handleCancellationOptionChange = (id: string) => {
    if (deleteOrderDescription) {
      const updateDeleteOrderDesc: DeleteOrderDto = {
        ...deleteOrderDescription,
        cancelReason: id ? findOrderCancellationLookupCode[id] : ''
      }
      setDeleteOrderDescription(updateDeleteOrderDesc)
    }
  }

  const handleCancellationTextChange = (comment: string) => {
    if (deleteOrderDescription) {
      const updateOrderLineDto: DeleteOrderDto = {
        ...deleteOrderDescription,
        cancelComments: comment
      }
      setDeleteOrderDescription(updateOrderLineDto)
    }
  }

  const handleSubmitCancellation = async () => {
    if (order && deleteOrderDescription) {
      setCancellationSubmitting(true)
      setIsLoadingCancelOrder(true)

      try {
        // Extract required data from order
        const orderItem = order.lines.find((x) => x.medicationFlag === 'Y')
        const resolvedPhysicianId =
          orderItem?.physician?.physicianId?.toString()
        const resolvedPatientNumber = orderItem?.patientNumber
        const resolvedProgramId = programId?.toString()
        const resolvedPatientId = history.location?.state?.patientId

        // Resolve shipping address ID from multiple sources
        const resolvedShippingAddressId =
          order.deliverTo?.addressId ||
          institute.data?.shipTo?.find((addr) => addr.primaryFlag === 'Y')
            ?.addressId ||
          ''

        if (
          !resolvedPhysicianId ||
          !resolvedPatientNumber ||
          !resolvedProgramId
        ) {
          const missingFields = []
          if (!resolvedPhysicianId) missingFields.push('physicianId')
          if (!resolvedPatientNumber) missingFields.push('patientNumber')
          if (!resolvedProgramId) missingFields.push('programId')

          console.error(
            `Missing required data for order cancellation: ${missingFields.join(
              ', '
            )}. Order: ${orderId}`
          )
          throw new Error(
            'Unable to cancel order due to missing required information.'
          )
        }

        // Log warning if shipping address ID is missing
        if (!resolvedShippingAddressId) {
          console.warn(
            `Warning: No shipping address ID found for order ${orderId}. ` +
              `This may cause deleteOpa to fail. Checked sources: ` +
              `order.deliverTo.addressId=${order.deliverTo?.addressId}, ` +
              `institute primary address=${
                institute.data?.shipTo?.find((addr) => addr.primaryFlag === 'Y')
                  ?.addressId
              }`
          )
        }

        // Create cancellation data using the saga helper
        const cancellationData =
          OrderCancellationSaga.createSimpleOrderCancellation(
            orderId,
            resolvedPhysicianId,
            resolvedPatientNumber,
            resolvedProgramId,
            resolvedShippingAddressId,
            'InitialOrder', // orderType - may need to be determined from order
            deleteOrderDescription.cancelReason,
            deleteOrderDescription.cancelComments || ''
          )

        // Add patient ID if available
        if (resolvedPatientId) {
          cancellationData.patientId = resolvedPatientId
        }

        // Execute the saga with monitoring utilities
        const transactionId = `opa_summary_${orderId}_${Date.now()}`
        const sagaLogger = createOrderCancellationLogger(transactionId)

        const saga = new OrderCancellationSaga(sagaLogger)
        const result = await saga.cancelOrder(cancellationData)

        // Log the result for monitoring
        logOrderCancellationResult(result)

        if (result.success && result.result) {
          // Check if deleteOpa was successful
          if (!result.result.opaDeleted) {
            console.warn(
              `Order cancellation completed but deleteOpa failed for order ${orderId}. ` +
                `This may cause cross-system validation issues. Manual cleanup may be required.`
            )
          }

          setIsCancelOrderlineModalOpen(false)
          analyticsServiceSingleton.trackEvent(AnalyticsEvent.CancelOrderMA, {
            programId,
            orderId,
            location: 'Ma Order Summary'
          })
          history.push('/orders')
        } else {
          // Log detailed error information to console
          const errorMessage =
            result.error?.message || 'Order cancellation failed'
          console.error(`Order cancellation failed for order ${orderId}:`, {
            error: errorMessage,
            orderNumber: orderId,
            patientNumber: resolvedPatientNumber,
            programId: resolvedProgramId,
            shippingAddressId: resolvedShippingAddressId,
            result: result
          })

          // Show generic error to user
          throw new Error(
            'Order cancellation failed. Please try again or contact support if the issue persists.'
          )
        }
      } catch (error) {
        // If request is cancelled continue
        if (
          error instanceof Error &&
          error.message === AuthError.RequestCancelled
        ) {
          return
        }

        // Log detailed error information to console
        console.error(`Order cancellation error for order ${orderId}:`, {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          orderNumber: orderId,
          timestamp: new Date().toISOString()
        })

        // Show generic error message to user
        const userErrorMessage =
          error instanceof Error &&
          error.message.includes(
            'Unable to cancel order due to missing required information'
          )
            ? error.message
            : 'There was an error when cancelling your order. Please try again or contact support if the issue persists.'

        setCancellationHasError({
          hasError: true,
          errorMessage: userErrorMessage
        })
        analyticsServiceSingleton.trackError(
          error instanceof Error ? error : new Error(String(error)),
          JSON.parse(localStorage.getItem('current_user') || '{}')
        )
      } finally {
        setCancellationSubmitting(false)
        setIsLoadingCancelOrder(false)
      }
    }
  }

  const handleCancellationClose = () => {
    setIsCancelOrderlineModalOpen(false)
    setDeleteOrderDescription(undefined)
  }

  //Stores state of accordions on the page, Expands all before calling print, Collapses to previous state
  //Handles hiding of the wootric survey as well
  const handlePrint = () => {
    const accoridonPanels = document.querySelectorAll('.accordion-panel')
    const wootricModal = document.querySelector('.wootric-desktop-modal')
    const state: number[] = []

    /**If theres accordions on page expand and save state */
    if (accoridonPanels) {
      for (let index = 0; index < accoridonPanels.length; index++) {
        state[index] = accoridonPanels[index].hasAttribute('hidden') ? 1 : 0
      }

      accoridonPanels.forEach((panel) => {
        panel.removeAttribute('hidden')
      })
    }

    wootricModal?.setAttribute('hidden', 'hidden')
    /**Actual print */
    window.print()
    /**Collapse to previous state */
    if (accoridonPanels) {
      for (let index = 0; index < accoridonPanels.length; index++) {
        if (state[index])
          accoridonPanels[index].setAttribute('hidden', 'hidden')
      }
    }
    wootricModal?.removeAttribute('hidden')
  }

  return (
    <>
      <OrderLineCancellationModal
        isOpen={isCancelOrderlineModalOpen}
        selectedOption={
          deleteOrderDescription &&
          findOrderCancellationLookupCode[deleteOrderDescription?.cancelReason]
        }
        cancellationOptions={cancellationOptions}
        isSubmitting={cancellationSubmitting}
        handleCommentChange={handleCancellationTextChange}
        handleOptionChange={handleCancellationOptionChange}
        handleSubmit={handleSubmitCancellation}
        handleClose={handleCancellationClose}
        hasError={cancellationHasError && cancellationHasError.hasError}
        errorMessage={cancellationHasError && cancellationHasError.errorMessage}
      />
      <OpaOrderSummary
        isLoading={
          isOrderLoading ||
          isLoadingPatientAccessForm ||
          isProgramLoading ||
          isLoadingCancelOrder
        }
        isOrderCancellable={order && isOrderCancellable(order)}
        order={order}
        program={program}
        orderLevelHolds={orderLevelHolds}
        patientAccessFormUrl={patientAccessFormUrl}
        handlePatientAccessForm={handlePatientAccessForm}
        handleCancelOrder={handleCancelOrder}
        handlePlaceOrderAgain={handlePlaceOrderAgain}
        handleViewDashboard={handleViewDashboard}
        handlePrint={handlePrint}
        isMaOrder={false}
        deliveryNote={deliveryNote}
        institute={institute.data?.instituteName}
      />
    </>
  )
}
